import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Users, TrendingUp, CircleDollarSign, Repeat, Sparkles,
  Download, Search, MessageSquareQuote, LayoutGrid, Loader2, Mail, Crown,
  AlertTriangle, TrendingDown, ExternalLink,
} from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip,
  CartesianGrid, ReferenceLine,
} from "recharts";

// ---------- helpers ----------
const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return iso.slice(0, 10); }
};
const fmtShortDate = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("pt-BR", { day: "2-digit", month: "short" }); }
  catch { return iso.slice(5, 10); }
};
const fmtCurrency = (val, cur) => {
  const upper = (cur || "BRL").toUpperCase();
  try {
    return new Intl.NumberFormat(upper === "BRL" ? "pt-BR" : "en-US", { style: "currency", currency: upper, maximumFractionDigits: 0 }).format(val || 0);
  } catch { return `${upper} ${(val || 0).toFixed(0)}`; }
};

// ---------- shared UI ----------
const KpiCard = ({ label, value, sub, icon: Icon, tone = "zinc", testId, delta }) => {
  const tones = {
    zinc: "bg-white border-zinc-200 text-zinc-900",
    orange: "bg-[#FF4F00] border-[#FF4F00] text-white",
    black: "bg-zinc-900 border-zinc-900 text-white",
    green: "bg-emerald-50 border-emerald-200 text-emerald-900",
    red: "bg-red-50 border-red-200 text-red-900",
  };
  const light = tone === "zinc" || tone === "green" || tone === "red";
  return (
    <div data-testid={testId} className={`rounded-xl border p-5 ${tones[tone]}`}>
      <div className="flex items-start justify-between mb-3">
        <div className={`text-[11px] font-bold uppercase tracking-widest ${light ? "text-zinc-500" : "text-white/70"}`}>
          {label}
        </div>
        {Icon && <Icon size={18} className={light ? "text-zinc-400" : "text-white/70"} />}
      </div>
      <div className="text-3xl font-black tracking-tighter leading-none flex items-baseline gap-2">
        {value}
        {delta && (
          <span className={`text-xs font-bold ${
            delta.trend === "up" ? "text-emerald-600" : delta.trend === "down" ? "text-red-600" : "text-zinc-400"
          }`}>{delta.label}</span>
        )}
      </div>
      {sub && <div className={`mt-2 text-xs ${light ? "text-zinc-500" : "text-white/70"}`}>{sub}</div>}
    </div>
  );
};

const PlanBadge = ({ plan }) => (
  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wider ${
    plan && plan !== "free" ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-500 border border-zinc-200"
  }`}>
    {plan && plan !== "free" && <Crown size={11} />} {plan}
  </span>
);

// ---------- MRR chart ----------
function MrrChart({ series }) {
  const data = (series || []).map((p) => ({
    date: p.date,
    mrr: p.mrr_total_brl_equiv || 0,
    subs: p.active_subs || 0,
    label: fmtShortDate(p.date),
  }));
  const latest = data[data.length - 1]?.mrr || 0;
  const first = data[0]?.mrr || 0;
  const growth = first > 0 ? (((latest - first) / first) * 100).toFixed(1) : (latest > 0 ? 100 : 0);

  return (
    <div data-testid="admin-mrr-chart" className="bg-zinc-900 text-white rounded-xl p-6">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-widest text-white/60">MRR (últimos 90 dias)</div>
          <div className="mt-1 flex items-baseline gap-3">
            <span className="text-4xl font-black tracking-tighter">{fmtCurrency(latest, "brl")}</span>
            <span className={`text-sm font-bold ${growth >= 0 ? "text-emerald-400" : "text-red-400"}`}>
              {growth >= 0 ? "▲" : "▼"} {Math.abs(growth)}%
            </span>
          </div>
          <div className="text-xs text-white/50 mt-1">
            Valores convertidos pra BRL (US$ × 5). Anuais divididos por 12.
          </div>
        </div>
        <span className="text-xs text-white/50">{data.length} pontos</span>
      </div>
      <div className="h-52 -mx-2">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 6, right: 12, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="mrrGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#FF4F00" stopOpacity={0.55} />
                <stop offset="100%" stopColor="#FF4F00" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="#27272a" strokeDasharray="2 4" vertical={false} />
            <XAxis
              dataKey="label"
              stroke="#71717a"
              fontSize={10}
              tickLine={false}
              axisLine={false}
              interval={Math.max(0, Math.floor(data.length / 8) - 1)}
            />
            <YAxis
              stroke="#71717a"
              fontSize={10}
              tickLine={false}
              axisLine={false}
              width={50}
              tickFormatter={(v) => `R$${v}`}
            />
            <Tooltip
              contentStyle={{ background: "#0A0A0A", border: "1px solid #27272a", borderRadius: 8, fontSize: 12 }}
              labelStyle={{ color: "#a1a1aa" }}
              formatter={(value, name) => name === "mrr" ? [fmtCurrency(value, "brl"), "MRR"] : [value, "Assinaturas"]}
            />
            <Area type="monotone" dataKey="mrr" stroke="#FF4F00" strokeWidth={2} fill="url(#mrrGradient)" />
            <ReferenceLine y={0} stroke="#27272a" />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// ---------- late payments card ----------
function LatePaymentsPanel({ late }) {
  if (!late) return null;
  return (
    <div data-testid="admin-late-payments" className="bg-white rounded-xl border border-zinc-200 overflow-hidden">
      <div className="px-5 py-4 border-b border-zinc-200 flex items-center gap-3">
        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
          late.count > 0 ? "bg-red-50 text-red-600" : "bg-emerald-50 text-emerald-600"
        }`}>
          <AlertTriangle size={16} />
        </div>
        <div className="flex-1">
          <h3 className="font-heading font-black text-base tracking-tight">Pagamentos atrasados</h3>
          <p className="text-xs text-zinc-500">Pro com assinatura vencida (grace de 3 dias).</p>
        </div>
        <span className="text-2xl font-black tracking-tighter">{late.count}</span>
      </div>
      {late.count === 0 ? (
        <div className="p-6 text-center text-sm text-zinc-500">
          Tudo em dia. Nenhum Pro está com pagamento em atraso. 🎉
        </div>
      ) : (
        <ul className="divide-y divide-zinc-100">
          {late.users.map((u) => (
            <li key={u.user_id} data-testid={`late-row-${u.user_id}`} className="px-5 py-3 flex items-center gap-3">
              {u.picture ? (
                <img src={u.picture} alt="" referrerPolicy="no-referrer" className="w-9 h-9 rounded-full flex-shrink-0" />
              ) : (
                <div className="w-9 h-9 rounded-full bg-red-100 text-red-700 flex items-center justify-center text-xs font-bold flex-shrink-0">
                  {(u.name || u.email || "?")[0]?.toUpperCase()}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm truncate">{u.name || u.email}</div>
                <div className="text-xs text-zinc-500 truncate">{u.email}</div>
              </div>
              <div className="text-right">
                {u.reason === "no_payment" ? (
                  <span className="inline-block bg-amber-50 text-amber-700 text-[11px] font-bold uppercase tracking-wider px-2 py-1 rounded-full border border-amber-200">
                    sem pagto.
                  </span>
                ) : (
                  <span className="inline-block bg-red-50 text-red-700 text-[11px] font-bold uppercase tracking-wider px-2 py-1 rounded-full border border-red-200">
                    {u.days_late}d atraso
                  </span>
                )}
                <a
                  href={`mailto:${u.email}?subject=Sua%20assinatura%20depoia&body=Oi%20${encodeURIComponent(u.name || "")}%2C%20notei%20que%20sua%20assinatura%20est%C3%A1%20com%20pagamento%20pendente...`}
                  className="block text-[11px] text-zinc-500 hover:text-[#FF4F00] mt-1 flex items-center justify-end gap-0.5"
                >
                  cobrar <ExternalLink size={10} />
                </a>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---------- main ----------
export default function Admin() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState(null);
  const [mrr, setMrr] = useState(null);
  const [churn, setChurn] = useState(null);
  const [late, setLate] = useState(null);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!loading && (!user || !user.is_admin)) {
      navigate("/dashboard", { replace: true });
    }
  }, [loading, user, navigate]);

  const fetchInsights = async () => {
    setBusy(true);
    try {
      const [s, m, ch, la] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/mrr-history?days=90"),
        api.get("/admin/churn?days=30"),
        api.get("/admin/late-payments"),
      ]);
      setStats(s.data);
      setMrr(m.data);
      setChurn(ch.data);
      setLate(la.data);
    } catch {
      toast.error("Erro ao carregar dados de admin.");
    } finally {
      setBusy(false);
    }
  };

  const fetchUsers = async (search = "") => {
    try {
      const r = await api.get(`/admin/users${search ? `?q=${encodeURIComponent(search)}` : ""}`);
      setUsers(r.data.users);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    if (user?.is_admin) {
      fetchInsights();
      fetchUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.is_admin]);

  useEffect(() => {
    if (!user?.is_admin) return;
    const id = setTimeout(() => fetchUsers(q), 350);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const mrrEntries = useMemo(() => Object.entries(stats?.mrr || {}), [stats]);
  const primaryMrr = mrrEntries.find(([c]) => c === "brl") || mrrEntries[0];

  const mrrDelta = useMemo(() => {
    if (!mrr?.series?.length) return null;
    const arr = mrr.series;
    const last = arr[arr.length - 1]?.mrr_total_brl_equiv || 0;
    const prev = arr[Math.max(0, arr.length - 31)]?.mrr_total_brl_equiv || 0;
    if (prev === 0 && last === 0) return null;
    const pct = prev === 0 ? 100 : ((last - prev) / prev) * 100;
    return { trend: pct >= 0 ? "up" : "down", label: `${pct >= 0 ? "+" : ""}${pct.toFixed(0)}% vs 30d` };
  }, [mrr]);

  const exportCsv = async () => {
    try {
      const r = await api.get("/admin/users/export", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `depoia-usuarios-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV baixado!");
    } catch {
      toast.error("Erro ao exportar.");
    }
  };

  if (loading || !user?.is_admin) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center text-sm text-zinc-400">
        <Loader2 className="animate-spin mr-2" size={16} /> Carregando painel admin...
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Header */}
      <header className="border-b border-zinc-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link
              to="/dashboard"
              data-testid="admin-back-link"
              className="text-sm text-zinc-500 hover:text-zinc-900 transition-colors flex items-center gap-1.5"
            >
              <ArrowLeft size={15} /> Painel
            </Link>
            <div className="hidden sm:block h-4 w-px bg-zinc-200" />
            <div className="hidden sm:flex items-center gap-2">
              <div className="w-6 h-6 rounded-md bg-zinc-900 text-white flex items-center justify-center">
                <Sparkles size={13} />
              </div>
              <h1 className="font-heading font-black text-lg tracking-tight">Admin</h1>
            </div>
          </div>
          <button
            data-testid="admin-export-btn"
            onClick={exportCsv}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-zinc-300 rounded-full text-sm font-semibold text-zinc-800 hover:border-zinc-500 transition-colors"
          >
            <Download size={14} /> Exportar CSV
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10 space-y-10">
        {/* KPIs */}
        <section>
          <div className="flex items-baseline justify-between mb-4">
            <h2 className="font-heading font-black text-2xl tracking-tight">Visão geral</h2>
            <span className="text-xs text-zinc-400">
              {stats?.as_of ? `atualizado ${fmtDate(stats.as_of)}` : ""}
            </span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KpiCard
              testId="kpi-mrr"
              label="MRR"
              tone="orange"
              icon={CircleDollarSign}
              value={primaryMrr ? fmtCurrency(primaryMrr[1], primaryMrr[0]) : "R$ 0"}
              sub={
                mrrEntries.length > 1
                  ? `+ ${mrrEntries.filter(([c]) => c !== (primaryMrr?.[0])).map(([c, v]) => fmtCurrency(v, c)).join(" · ")}`
                  : `${stats?.active_paying_subscriptions || 0} assinaturas pagas`
              }
              delta={mrrDelta}
            />
            <KpiCard
              testId="kpi-conversion"
              label="Conversão free → pro"
              tone="black"
              icon={TrendingUp}
              value={`${stats?.conversion_rate ?? 0}%`}
              sub={`${stats?.totals?.pro || 0} de ${stats?.totals?.users || 0} contas`}
            />
            <KpiCard
              testId="kpi-churn"
              label="Churn (30d)"
              tone={churn?.churn_rate > 5 ? "red" : "green"}
              icon={TrendingDown}
              value={`${churn?.churn_rate ?? 0}%`}
              sub={`${churn?.churned ?? 0} cancelou · ${churn?.active_now ?? 0} ativos`}
            />
            <KpiCard
              testId="kpi-late"
              label="Pagto. atrasado"
              tone={late?.count > 0 ? "red" : "green"}
              icon={AlertTriangle}
              value={late?.count ?? 0}
              sub={late?.count > 0 ? "clique no painel abaixo pra agir" : "todos em dia"}
            />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
            <KpiCard testId="kpi-users" label="Contas totais" tone="zinc" icon={Users}
              value={stats?.totals?.users ?? 0}
              sub={`+${stats?.signups?.last_7d ?? 0} nos últimos 7 dias`} />
            <KpiCard testId="kpi-pro" label="Assinantes Pro" tone="zinc" icon={Crown}
              value={stats?.totals?.pro ?? 0} />
            <KpiCard testId="kpi-testimonials" label="Depoimentos" tone="zinc" icon={MessageSquareQuote}
              value={stats?.totals?.testimonials ?? 0}
              sub={`${stats?.totals?.testimonials_approved ?? 0} aprovados`} />
            <KpiCard testId="kpi-campaigns" label="Campanhas ativas" tone="zinc" icon={LayoutGrid}
              value={stats?.totals?.campaigns ?? 0} />
          </div>
        </section>

        {/* Chart + late payments */}
        <section className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            {mrr ? <MrrChart series={mrr.series} /> : (
              <div className="bg-zinc-900 rounded-xl p-6 h-64 flex items-center justify-center text-white/40">
                <Loader2 className="animate-spin" size={18} />
              </div>
            )}
          </div>
          <LatePaymentsPanel late={late} />
        </section>

        {/* Users table */}
        <section>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <div>
              <h2 className="font-heading font-black text-2xl tracking-tight">Usuários</h2>
              <p className="text-sm text-zinc-500 mt-1">
                {users ? `${users.length} contas ${q ? `para "${q}"` : "no total"}` : "Carregando..."}
              </p>
            </div>
            <div className="relative">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <input
                data-testid="admin-search-input"
                type="search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Buscar por nome ou email..."
                className="pl-9 pr-4 py-2.5 w-full sm:w-72 bg-white border border-zinc-200 rounded-full text-sm focus:ring-2 focus:ring-[#FF4F00]/40 focus:border-[#FF4F00] focus:outline-none"
              />
            </div>
          </div>

          <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
            {busy && !users && (
              <div className="p-10 text-center text-sm text-zinc-400 flex items-center justify-center gap-2">
                <Loader2 className="animate-spin" size={14} /> carregando...
              </div>
            )}
            {users && users.length === 0 && (
              <div data-testid="admin-users-empty" className="p-10 text-center text-sm text-zinc-500">
                Nenhum usuário encontrado.
              </div>
            )}
            {users && users.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-zinc-50 text-zinc-500 text-[11px] uppercase tracking-widest font-semibold">
                    <tr>
                      <th className="text-left py-3 px-4">Usuário</th>
                      <th className="text-left py-3 px-3">Plano</th>
                      <th className="text-left py-3 px-3">Cadastro</th>
                      <th className="text-right py-3 px-3">Campanhas</th>
                      <th className="text-right py-3 px-3">Depoimentos</th>
                      <th className="text-right py-3 px-4">Último pagamento</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u.user_id} data-testid={`admin-user-row-${u.user_id}`} className="border-t border-zinc-100 hover:bg-zinc-50/60 transition-colors">
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-3 min-w-0">
                            {u.picture ? (
                              <img src={u.picture} alt="" referrerPolicy="no-referrer" className="w-8 h-8 rounded-full flex-shrink-0" />
                            ) : (
                              <div className="w-8 h-8 rounded-full bg-zinc-200 text-zinc-500 flex items-center justify-center text-xs font-bold flex-shrink-0">
                                {(u.name || u.email || "?").trim()[0]?.toUpperCase()}
                              </div>
                            )}
                            <div className="min-w-0">
                              <div className="font-semibold text-zinc-900 truncate max-w-[220px]">{u.name || "—"}</div>
                              <a href={`mailto:${u.email}`} className="text-xs text-zinc-500 hover:text-[#FF4F00] truncate flex items-center gap-1 max-w-[220px]">
                                <Mail size={11} /> {u.email}
                              </a>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-3"><PlanBadge plan={u.plan} /></td>
                        <td className="py-3 px-3 text-zinc-600">{fmtDate(u.created_at)}</td>
                        <td className="py-3 px-3 text-right font-semibold tabular-nums">{u.campaigns_count}</td>
                        <td className="py-3 px-3 text-right font-semibold tabular-nums">{u.testimonials_count}</td>
                        <td className="py-3 px-4 text-right">
                          {u.last_payment ? (
                            <div className="text-xs">
                              <div className="font-semibold tabular-nums">{fmtCurrency(u.last_payment.amount, u.last_payment.currency)}</div>
                              <div className="text-zinc-500">{u.last_payment.lookup_key.replace(/^pro_/, "").replace("_usd", " · USD")}</div>
                            </div>
                          ) : (
                            <span className="text-zinc-300 text-xs">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>

        <div className="text-center text-xs text-zinc-400 pt-4">
          <Repeat size={12} className="inline mr-1" />
          <button
            data-testid="admin-refresh-btn"
            onClick={() => { fetchInsights(); fetchUsers(q); }}
            className="hover:text-zinc-800 transition-colors"
          >
            Atualizar dados
          </button>
        </div>
      </main>
    </div>
  );
}
